import { FC } from 'react';
import { HStack, Stack } from '@chakra-ui/react';
import { formatSIRET } from '@shinetools/vat-siren-siret';

import { ClientType } from '__generated__/GQL';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import { useModal } from 'components/ModalProvider/hooks';
import { getAddressString } from 'features/Onboarding/common/getAddressString';
import ClientAvatar from 'features/Work/pages/Clients/components/ClientAvatar';

import { useClientModalContext } from '../../../../components/Modals';
import { ClientModalType } from '../../../../components/Modals/types';
import ListItemMenu from '../../../List/components/ListItem/components/ListItemMenu';
import { Client } from '../../types';
import ContactsInfoLine from './components/ContactsInfoLine';
import locales from './locales';

interface DetailsHeaderCardProps {
  client: Client;
}

const DetailsHeaderCard: FC<DetailsHeaderCardProps> = ({ client }) => {
  const { showModal } = useModal<ClientModalType>();
  const { setContact } = useClientModalContext();

  const {
    city,
    companyName,
    contacts,
    legalRegistrationNumber,
    name,
    street,
    type: clientType,
    vatId,
    zip,
  } = client;

  const address = getAddressString({ city, street, zip });

  const isIndividual = clientType === ClientType.PrivateIndividual;

  return (
    <SunshineCard gap="space-24" padding="space-24">
      <SunshineCard.Slot name="aside">
        <ClientAvatar name={name} />
      </SunshineCard.Slot>

      <SunshineCard.Content spacing="space-16">
        <Stack spacing="space-4">
          <Heading>{companyName || name}</Heading>

          {isIndividual ? null : (
            <>
              <Text>
                {!legalRegistrationNumber
                  ? locales.formatString(locales.noSiret)
                  : locales.formatString(
                      locales.siret,
                      formatSIRET(legalRegistrationNumber),
                    )}
              </Text>

              <Text>
                {!vatId
                  ? locales.formatString(locales.noVatNumber)
                  : locales.formatString(locales.vatNumber, vatId)}
              </Text>
            </>
          )}
        </Stack>

        <Stack spacing="space-8">
          <HStack alignItems="center" width="100%">
            <SunshineIcon color="text-primary" name="home" size="icon-24" />

            <Text flex={1} variant="primary" whiteSpace="inherit">
              {address || locales.noAddress}
            </Text>
          </HStack>

          <HStack alignItems="center" width="100%">
            <SunshineIcon color="text-primary" name="profile" size="icon-24" />

            <ContactsInfoLine
              contacts={contacts}
              onContactClick={(contact) => {
                setContact(contact);
                showModal(ClientModalType.EditContact);
              }}
            />
          </HStack>
        </Stack>
      </SunshineCard.Content>

      <SunshineCard.Slot name="action">
        <ListItemMenu client={client} />
      </SunshineCard.Slot>
    </SunshineCard>
  );
};

export default DetailsHeaderCard;
